import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { type ChangeEvent, createContext, type FormEvent, type InputHTMLAttributes, type MouseEvent, useContext } from 'react';
import { Bounce, toast, type ToastOptions } from 'react-toastify';
import * as yup from 'yup';

import { reduxPersistor } from '@/lib';

import {
	type COUNTRYTYPE,
	type CURRENCYCODETYPE,
	CURRENCYCONFIG,
	type CURRENCYCONFIGTYPE,
	type CURRENCYSHORTTYPE,
	type CURRENCYSYMBOLTYPE,
	FXCONVERSION,
	PINLENGTH,
} from '../constants';
import {
	type ApiFulfilledAction,
	type AuthSettingsPayload,
	type Breadcrumb,
	type CountryProps,
	type DateRange,
	type FileType,
	type FXCONVERSION_TYPE,
	type IApiResponse,
	type IApiResponseError,
	type IAppStatus,
	type IAuthSettingsUSDWithdrawalMeta,
	type IBank,
	type ICurrencyConfig,
	type IDropDownItem,
	type IFiatMetaProps,
	type ISettings,
	type ISettingsFXRates,
	type ITransactionHistory,
	type ITransactionStatus,
	type IUseFormStage,
	type IUser,
	type IUserWallet,
	type IWalletInfoOptionProps,
	type IWalletProps,
	type IWebhookLogsEventType,
	type IWebhookLogsStatusType,
	type NetworkType,
	type TokenType,
	type TransactionHistoryType,
	type UploadErrorPayload,
	type UploadQueryReturnValue,
	type windowSizeType,
} from '../types';

export const fileTypeToMimeMap: Record<FileType, string> = {
	'pdf': 'application/pdf',
	'doc': 'application/msword',
	'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'xls': 'application/vnd.ms-excel',
	'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'ppt': 'application/vnd.ms-powerpoint',
	'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'txt': 'text/plain',
	'csv': 'text/csv',
	'rtf': 'application/rtf',
	'odt': 'application/vnd.oasis.opendocument.text',
	'html': 'text/html',
	'md': 'text/markdown',

	'jpg': 'image/jpeg',
	'jpeg': 'image/jpeg',
	'png': 'image/png',
	'gif': 'image/gif',
	'bmp': 'image/bmp',
	'webp': 'image/webp',
	'svg': 'image/svg+xml',
	'tiff': 'image/tiff',
	'heic': 'image/heic',

	'mp3': 'audio/mpeg',
	'wav': 'audio/wav',
	'ogg': 'audio/ogg',
	'flac': 'audio/flac',
	'aac': 'audio/aac',

	'mp4': 'video/mp4',
	'mov': 'video/quicktime',
	'avi': 'video/x-msvideo',
	'mkv': 'video/x-matroska',
	'webm': 'video/webm',
	'wmv': 'video/x-ms-wmv',

	'zip': 'application/zip',
	'rar': 'application/vnd.rar',
	'7z': 'application/x-7z-compressed',
	'tar': 'application/x-tar',
	'gz': 'application/gzip',

	'exe': 'application/vnd.microsoft.portable-executable',
	'apk': 'application/vnd.android.package-archive',
	'dmg': 'application/x-apple-diskimage',
	'iso': 'application/x-iso9660-image',
};

export const ToastPosition: ToastOptions = {
	position: 'top-right',
	autoClose: 2000,
	hideProgressBar: true,
	closeOnClick: true,
	rtl: false,
	pauseOnFocusLoss: true,
	draggable: true,
	pauseOnHover: true,
	progress: undefined,
	theme: 'colored',
	transition: Bounce,
};

export const getFileExtensionByMimeType = (mimeType: string): FileType | undefined => {
	for (const [ext, mime] of Object.entries(fileTypeToMimeMap)) {
		if (mime === mimeType) return ext as FileType;
	}
	return undefined;
};

export const showToast = (type: 'success' | 'error', message: string) => {
	switch (type) {
		case 'success':
			toast.success(capitalizeFirstLetter(message), ToastPosition);
			break;
		case 'error':
			toast.error(capitalizeFirstLetter(message), ToastPosition);
			break;
		default:
			throw new Error(`Invalid toast type: ${type as string}`);
	}
};

export const getLocationFrom = () => {
	const { pathname } = window.location;
	const searchParams = window.location.search;
	return encodeURIComponent(`${pathname}${searchParams}`);
};

export * from './kyb';

export const strictEmailValidation = (email: string): boolean => {
	const emailRegex =
		/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

	if (!emailRegex.test(email)) return false;

	const [localPart, domain] = email.split('@');

	if (localPart.length > 64) return false;
	if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
	if (localPart.includes('..')) return false;

	if (domain.length > 253) return false;
	if (domain.startsWith('-') || domain.endsWith('-')) return false;
	if (domain.includes('..')) return false;

	const tldParts = domain.split('.');
	const tld = tldParts[tldParts.length - 1];
	if (tld.length < 2) return false;

	return true;
};

export const emailValidation = yup
	.string()
	.required('Email is required')
	.trim()
	.lowercase()
	.min(3, 'Email must be at least 3 characters')
	.max(254, 'Email must not exceed 254 characters')
	.matches(
		/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/,
		'Please enter a valid email address'
	);

export const capitalizeFirstLetters = (sentence = '') => {
	const words = sentence.split(' ');
	const capitalizedWords = words.map((word) => word[0].toUpperCase() + word.slice(1));
	const capitalizedSentence = capitalizedWords.join(' ');
	return capitalizedSentence;
};

export const GenerateBreadcrumb = (pathname = '') => {
	const segments = pathname.split('/').filter((segment) => segment);

	const breadcrumb: Breadcrumb[] = segments.map((segment, index) => {
		const segmentPath = `/${segments.slice(0, index + 1).join('/')}`;

		let segmentLabel = capitalizeFirstLetters(segment.replace(/-+/g, ' '));

		segmentLabel = segmentLabel.replace(/\bId\b/g, 'ID');

		return { label: segmentLabel, url: segmentPath };
	});

	return breadcrumb;
};

export const defaultFiatMetaProps: IFiatMetaProps = {
	country: 'Nigeria',
	countryLower: 'nigeria',
	currency: 'NGN',
	currencyLower: 'ngn',
	currencySymbol: CURRENCYCONFIG.NGN.symbol,
	currencyShort: 'naira',
};

export const capitalize = (word: string): string => {
	if (!word) return '';
	return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export const getFiatPropsByCountry = (country: string): ICurrencyConfig | undefined =>
	Object.values(CURRENCYCONFIG).find((v) => v.country.toLowerCase() === country.toLowerCase());

export const getFxRateByCountry = (country: Lowercase<COUNTRYTYPE>, fxRates?: ISettingsFXRates) => {
	if (!fxRates) return 0;

	const rate = fxRates.NGNToUSDForVIP;

	if (country === 'ghana') {
		return rate / fxRates.NGNToGHS;
	}

	return rate;
};

export const setFiatMetaProps = (data: IApiResponse<IUser>): IFiatMetaProps => {
	const userData = data.data;
	const fxRates = data.meta?.settings.fxRates;
	const fiatProps = getFiatPropsByCountry(userData?.country ?? '');

	if (!userData || !fiatProps || !fxRates) return defaultFiatMetaProps;

	const countryLowerCase = userData.country.toLowerCase() as Lowercase<COUNTRYTYPE>;

	return {
		country: capitalize(userData.country) as COUNTRYTYPE,
		countryLower: countryLowerCase,
		currency: fiatProps.code as CURRENCYCODETYPE,
		currencyLower: fiatProps.code.toLowerCase() as Lowercase<CURRENCYCODETYPE>,
		currencySymbol: fiatProps.symbol as CURRENCYSYMBOLTYPE,
		currencyShort: fiatProps.currencyShort as CURRENCYSHORTTYPE,
	};
};

export const getInitials = (str: string): string => (str.match(/[A-Z]/g) ?? []).join('').toLowerCase();

export const otpValidation = yup.string().required('OTP is required').trim().matches(/^\d+$/, 'OTP must contain only numbers');

export const OTPVerificationSchema = yup.object({
	otp: otpValidation,
});

export const mapBankToDropDownItems = (item: IBank): IDropDownItem => ({
	code: getInitials(item.name),
	icon: item.avatar,
	name: item.name,
	value: item.name,
	id: item.id,
});

export interface IPhoneInputDropDown {
	searchTerm: string;
	handleSearchTerm?: (e: ChangeEvent<HTMLInputElement>) => void;
	handleCountrySelect?: (country: CountryProps) => (e: MouseEvent<HTMLButtonElement>) => void;
	selectedCountry: CountryProps;
}

export interface PhoneInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'placeholder'> {
	value?: string;
	placeholder?: string;
	defaultCountry?: string;
	label?: string;
	onChange?: (phone: string) => void;
	errorMessage?: string;
	hideSearch?: boolean;
}

export const formatDateTime = (isoString: string | undefined) => {
	const date = new Date(isoString ?? '');

	const dateStr = date.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});

	const timeStr = date.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		second: '2-digit',
	});

	return `${dateStr}, ${timeStr}`;
};

export const formatDateAndTime = (isoString: string) => {
	const dateObj = new Date(isoString);

	const date = dateObj.toLocaleDateString('en-US', {
		month: 'long',
		day: 'numeric',
		year: 'numeric',
	});

	const time = dateObj.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		second: '2-digit',
		hour12: true,
	});

	return { date, time };
};

export const ResetPasswordOTPSchema = yup.object({
	otp: otpValidation,
});

export const copyToClipboard = (text?: string, message?: string) => async () => {
	try {
		if (!text) throw new Error('');
		await navigator.clipboard.writeText(text);
		showToast('success', message ?? 'copied');
		return true;
	} catch (error) {
		console.error('Failed to copy text:', error);
		showToast('error', 'Failed to copy text');
		return false;
	}
};

export const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

export const copyText = async (text: string, message?: string): Promise<boolean> => {
	try {
		if (!text) throw new Error('');

		if (window.isSecureContext) {
			await navigator.clipboard.writeText(text);
			showToast('success', message ?? 'copied');
			return true;
		}

		const ta = document.createElement('textarea');
		ta.value = text;
		ta.setAttribute('readonly', '');
		ta.style.position = 'fixed';
		ta.style.top = '0';
		ta.style.left = '-9999px';
		ta.style.opacity = '0';
		document.body.appendChild(ta);
		ta.focus();
		ta.select();

		const copyHint = isMobile ? 'Tap and hold to copy.' : 'Press Ctrl/Cmd+C to copy.';
		showToast('error', `Automatic copy not available. ${copyHint}`);

		setTimeout(() => {
			if (document.body.contains(ta)) {
				document.body.removeChild(ta);
			}
		}, 5000);

		return false;
	} catch (error) {
		console.error('Failed to copy text:', error);
		showToast('error', 'Failed to copy text');
		return false;
	}
};

export const getLast7Days = (): DateRange => {
	const end = new Date();
	end.setUTCHours(0, 0, 0, 0);

	const start = new Date(end);
	start.setDate(end.getDate() - 6);

	return {
		startDate: start.toISOString(),
		endDate: end.toISOString(),
	};
};

export const formatToTime = (dateStr: string): string => {
	const date = new Date(dateStr);
	return date.toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: true,
	});
};

export const formatDateToReadable = (dateStr: string): string => {
	const date = new Date(dateStr);
	return new Intl.DateTimeFormat('en-US', {
		month: 'short',
		day: '2-digit',
		year: 'numeric',
	})
		.format(date)
		.toUpperCase();
};

export const isValidClass = (className?: string) => (className ? ` ${className}` : '');

export const mapPhoneCountryToDropDownItems = (item: CountryProps): IDropDownItem => ({
	code: item.code,
	icon: item.flag,
	name: item.name,
	value: item.dialCode,
	id: item.code,
});

export const mapCountryToDropDownItems = (item: CountryProps): IDropDownItem => ({
	code: item.code,
	icon: item.flag,
	name: item.name,
	value: item.name,
	id: item.name,
});

export const hideComponent = (windowSize: windowSizeType, screen: 'desktop' | 'mobile', size = 768) => {
	if (!windowSize.isClient) return { hideColumn: false };

	const isMobileArg = windowSize.width <= size;
	return { hideColumn: screen === 'desktop' ? !isMobileArg : isMobileArg };
};

export const getTransactionStatus = (status: ITransactionStatus) => {
	const obj: Record<ITransactionStatus, IAppStatus> = {
		completed: 'successful',
		deleted: 'unsuccessful',
		flagged: 'flagged',
		pending: 'pending',
		processing: 'processing',
		reversed: 'reversed',
	};

	return obj[status];
};

export const generateUEID = (): string => {
	const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
	const bytes = new Uint8Array(8);
	crypto.getRandomValues(bytes);

	return Array.from(bytes, (b) => chars[b % chars.length]).join('');
};

export const getMagnitudeText = (value: string | number): string => {
	const cleanedValue = String(value).replace(/[^0-9.-]/g, '');
	const num = Math.abs(parseFloat(cleanedValue));

	const ranges: { min: number; text: string }[] = [
		{ min: 100_000_000_000_000, text: 'Hundreds of Trillions' },
		{ min: 10_000_000_000_000, text: 'Tens of Trillions' },
		{ min: 1_000_000_000_000, text: 'Trillions' },
		{ min: 100_000_000_000, text: 'Hundreds of Billions' },
		{ min: 10_000_000_000, text: 'Tens of Billions' },
		{ min: 1_000_000_000, text: 'Billions' },
		{ min: 100_000_000, text: 'Hundreds of Millions' },
		{ min: 10_000_000, text: 'Tens of Millions' },
		{ min: 1_000_000, text: 'Millions' },
		{ min: 100_000, text: 'Hundreds of Thousands' },
		{ min: 10_000, text: 'Tens of Thousands' },
		{ min: 1_000, text: 'Thousands' },
		{ min: 100, text: 'Hundreds' },
		{ min: 10, text: 'Tens' },
		{ min: 1, text: 'Units' },
	];

	return Number.isNaN(num) || num === 0 ? '' : (ranges.find((range) => num >= range.min)?.text ?? '');
};

export const formatCurrencyInputAmount = (
	amount: string | number,
	currencyCode: CURRENCYCODETYPE,
	fractionDigits = 2
): string => {
	const amountStr = String(amount);
	const endsWithDot = amountStr.endsWith('.');

	const { locale } = CURRENCYCONFIG[currencyCode];
	let numericAmount: number;

	if (typeof amount === 'string') {
		const cleanedAmount = amount.replace(/[^\d.-]/g, '');
		numericAmount = parseFloat(cleanedAmount);
	} else {
		numericAmount = amount;
	}

	if (Number.isNaN(numericAmount)) {
		return '0';
	}

	const formattedAmount = numericAmount.toLocaleString(locale, {
		style: 'decimal',
		maximumFractionDigits: fractionDigits,
	});

	if (endsWithDot) {
		const decimalSeparator = (1.1).toLocaleString(locale).charAt(1);

		if (!formattedAmount.includes(decimalSeparator)) {
			return `${formattedAmount}${decimalSeparator}`;
		}
	}

	return formattedAmount;
};

export const formatCurrencyAmount = (amount: string | number, currencyCode: CURRENCYCODETYPE, fractionDigits = 2) => {
	const { locale, country } = CURRENCYCONFIG[currencyCode];

	let numericAmount: number;

	if (typeof amount === 'string') {
		const cleanedAmount = amount.replace(/[^\d.-]/g, '');
		numericAmount = parseFloat(cleanedAmount);
	} else {
		numericAmount = amount;
	}

	if (Number.isNaN(numericAmount)) {
		return `0.${'0'.repeat(fractionDigits)}`;
	}

	let formattedAmount = numericAmount.toLocaleString(locale, {
		style: 'currency',
		currency: currencyCode,
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	});

	if (country === 'Ghana') {
		formattedAmount = formattedAmount.replace('GH₵', '₵');
	}

	return formattedAmount;
};

export const getWalletProps = (currencyCode: CURRENCYCODETYPE | undefined, userWallet: IUserWallet[]): IWalletProps => {
	try {
		if (!currencyCode) throw new Error('currency code invalid');

		if (!Array.isArray(userWallet) || !userWallet[0]) throw new Error('wallet empty');

		const wallet = userWallet.find(
			(walletItem) => walletItem.type === 'fiat' && walletItem.currency === currencyCode.toLowerCase()
		);

		if (!wallet?.userInfo) throw new Error('wallet not found');

		const currencyAmount = formatCurrencyAmount(wallet.balance, currencyCode);

		return {
			currencyAmount,
			walletBalance: wallet.balance,
			walletId: wallet._id,
			name: wallet.userInfo.username,
			email: wallet.userInfo.email,
			symbol: CURRENCYCONFIG[currencyCode].symbol,
			code: CURRENCYCONFIG[currencyCode].code,
		};
	} catch (error) {
		console.error(error);

		return {
			currencyAmount: '0',
			walletBalance: 0,
			walletId: '',
			name: '',
			email: '',
			symbol: '',
			code: '',
		};
	}
};

export const getCurrencyProps = (user: IUser | undefined): CURRENCYCONFIGTYPE | undefined => {
	try {
		if (!user) throw new Error('userdata is not found');

		const isNgN = user.country === 'nigeria';

		const currencyCode: CURRENCYCODETYPE = isNgN ? 'NGN' : 'GHS';

		return CURRENCYCONFIG[currencyCode];
	} catch (error) {
		console.error(error);

		return undefined;
	}
};

export const getCleanedValue = (valueArg?: string) => (valueArg ? valueArg.replace(/[^\d.]/g, '') : '');

export const handleDecimalPoints = (cleanedValueArg: string) => {
	const decimalIndex = cleanedValueArg.indexOf('.');
	let cleanedValue = cleanedValueArg;
	if (decimalIndex !== -1) {
		cleanedValue = cleanedValue.substring(0, decimalIndex + 1) + cleanedValue.substring(decimalIndex + 1).replace(/\./g, '');
	}
	return cleanedValue;
};

const getWithdrawRulesAmount = (
	settings: ISettings,
	fromCurrency: CURRENCYCODETYPE | undefined,
	minNGNAmount: number,
	minGHSAmount: number,
	withdrawUsd?: boolean
) => {
	const isUsd = fromCurrency === 'USD';
	const isNgn = fromCurrency === 'NGN';

	const withdrawalMinFiatAmount = isNgn ? minNGNAmount : minGHSAmount;

	const withdrawalMinAmount = isUsd ? settings.usdWithdrawalMeta?.vip.minimum : withdrawalMinFiatAmount;

	const withdrawalMaxFiatAmount = isNgn
		? settings.withdrawalMeta.vip.withdrawal.maximum.nigeria
		: settings.withdrawalMeta.vip.withdrawal.maximum.ghana;

	const withdrawalMaxAmount = isUsd ? settings.usdWithdrawalMeta?.vip.maximum : withdrawalMaxFiatAmount;

	const withdrawUsdMin = settings.conversionMeta.USD.minimum;

	return {
		withdrawalMaxAmount: withdrawalMaxAmount ?? 0,
		withdrawalMinAmount: withdrawUsd ? withdrawUsdMin : (withdrawalMinAmount ?? 0),
	};
};

export const getWithdrawRules = (
	settings: ISettings | undefined,
	fromCurrency: CURRENCYCODETYPE | undefined,
	withdrawUsd?: boolean
) => {
	try {
		if (!settings) throw new Error('no settings config found');

		const minNGNAmount = settings.withdrawalMeta.vip.withdrawal.minima.nigeria;
		const minGHSAmount = settings.withdrawalMeta.vip.withdrawal.minima.ghana;

		return getWithdrawRulesAmount(settings, fromCurrency, minNGNAmount, minGHSAmount, withdrawUsd);
	} catch (error) {
		console.error(error);
		return {
			withdrawalMaxAmount: 0,
			withdrawalMinAmount: 0,
		};
	}
};

export const getSwapWithdrawRules = (
	settings: ISettings | undefined,
	fromCurrency: CURRENCYCODETYPE | undefined,
	withdrawUsd?: boolean
) => {
	try {
		if (!settings) throw new Error('no settings config found');

		const minNGNAmount = settings.currencySwapMeta.NGN.minimum;
		const minGHSAmount = settings.currencySwapMeta.GHS.minimum;

		return getWithdrawRulesAmount(settings, fromCurrency, minNGNAmount, minGHSAmount, withdrawUsd);
	} catch (error) {
		console.error(error);
		return {
			withdrawalMaxAmount: 0,
			withdrawalMinAmount: 0,
		};
	}
};

export const getFiatWithdrawFee = (settings: ISettings | undefined, currency: CURRENCYCODETYPE | undefined) => {
	try {
		if (!settings) throw new Error('no settings config found');

		const isNgn = currency === 'NGN';

		const withdrawalFeeFiatAmount = isNgn
			? settings.withdrawalMeta.vip.withdrawal.fee.nigeria
			: settings.withdrawalMeta.vip.withdrawal.fee.ghana;

		return withdrawalFeeFiatAmount;
	} catch (error) {
		console.error(error);
		return 0;
	}
};

export function formatNumberWithCommas(value: string | number): string {
	const num = typeof value === 'string' ? parseFloat(value) : value;

	if (Number.isNaN(num)) {
		return '0';
	}

	return num.toLocaleString('en-US');
}

export const trimLeadingZero = (value: string): string => {
	if (value === '0') return value;

	if (value.startsWith('0.')) return value;

	return value.replace(/^0+/, '');
};

export const toFixedPrecision = (value: string | number, decimalPlaces = 2): string => {
	const num = typeof value === 'string' ? parseFloat(value) : value;

	if (Number.isNaN(num) || !Number.isFinite(num) || decimalPlaces < 0 || decimalPlaces > 100) {
		return '0.' + '0'.repeat(Math.max(0, Math.min(decimalPlaces, 100)));
	}

	// Use native toFixed for simplicity and accuracy, then format the result
	const fixed = num.toFixed(decimalPlaces);

	// Ensure proper decimal places (toFixed already handles this, but be explicit)
	if (fixed.includes('.')) {
		const [integer, decimal] = fixed.split('.');
		return integer + '.' + decimal.padEnd(decimalPlaces, '0');
	}

	return fixed + '.' + '0'.repeat(decimalPlaces);
};

export const alwaysRefetchApiQueryOptions = {
	refetchOnMountOrArgChange: true,
	refetchOnReconnect: true,
};

export const pinValidation = yup
	.string()
	.required('PIN is required')
	.matches(/^\d+$/, 'PIN must contain only numbers')
	.length(PINLENGTH, `PIN must be exactly ${PINLENGTH} digits`);

export const capitalizeFirstLetter = (sentence?: string): string => {
	if (!sentence) return '';
	return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

export const mapArrayFromLength = (arrayLength: number) => Array.from({ length: arrayLength }).map((_, index) => index + 1);

export const PinVerificationSchema = yup.object().shape({
	pin: pinValidation,
});

export const getNumbericValue = (value: string) => Number(value.replace(/,/g, ''));

export const checkNearestValidAmount = (amount: number, currency: CURRENCYCODETYPE | undefined): string => {
	const isNGN = currency === 'NGN';

	const isUSD = currency === 'USD';

	const fiatBaseDivisor = isNGN ? 100 : 10;

	const baseDivisor = isUSD ? 1 : fiatBaseDivisor;

	if (amount % baseDivisor !== 0) {
		const nearestValidAmount = Math.round(amount / baseDivisor) * baseDivisor;
		const exceedsHundred = amount > 100;
		const adjustedDivisor = exceedsHundred ? 100 : baseDivisor;
		const displayDivisor = isNGN ? baseDivisor : adjustedDivisor;

		return `Invalid Amount, Please withdraw in nearest ${displayDivisor}s, e.g ${nearestValidAmount.toLocaleString()}.`;
	}

	return '';
};

export const getNearestValidAmount = (amount: number, isNGN: boolean): number => {
	const baseDivisor = isNGN ? 100 : 10;
	return Math.floor(amount / baseDivisor) * baseDivisor;
};

export const getApiError = (errorDataArg: unknown) => {
	try {
		const errorDataObject = errorDataArg as IApiResponseError | undefined;

		const errorData = errorDataObject?.data;

		if (!errorData) throw new Error('Error data not found');

		const { errors: apiErrors, message } = errorData;

		const apiError = apiErrors?.length ? apiErrors.map((i) => i.message).join(' & ') : undefined;

		if (apiError) return apiError;

		return message === 'invalid cookies passed' ? '' : message;
	} catch (err: unknown) {
		console.error(err);

		return '';
	}
};

export const handleApiError = (error: unknown) => {
	const apiError = getApiError(error);

	if (!apiError) return;

	showToast('error', apiError);
};

export const handleApiErrorWithKey = () => (error: unknown) => {
	const apiError = getApiError(error);

	if (!apiError) return;

	showToast('error', apiError);
};

export const selectedBeneficiarySchema = yup.object({
	id: yup.string().required().default(''),
	accountNumber: yup.string().required().default(''),
	accountName: yup.string().required().default(''),
	bankName: yup.string().required().default(''),
});

export const isWithdrawals = (row?: ITransactionHistory<TransactionHistoryType>) => row?.collection === 'withdrawals';

export const isEdits = (row?: ITransactionHistory<TransactionHistoryType>) => row?.collection === 'edits';

export const isConversions = (row?: ITransactionHistory<TransactionHistoryType>) => row?.collection === 'conversions';

export const isApiFulfilledAction = <TPayload = unknown>(action: unknown): action is ApiFulfilledAction<TPayload> => {
	if (
		typeof action === 'object' &&
		action !== null &&
		'type' in action &&
		typeof (action as Record<string, unknown>).type === 'string'
	) {
		const type = (action as Record<string, unknown>).type as string;
		return type.startsWith('api/') && type.endsWith('/fulfilled');
	}
	return false;
};

export const fulfilledWithSettingsMatcher = (action: unknown): action is ApiFulfilledAction<AuthSettingsPayload> =>
	isApiFulfilledAction<AuthSettingsPayload>(action);

export const emptyBeneficiaryState = {
	accountName: '',
	accountNumber: '',
	bankName: '',
	id: '',
};

export const updatePersistor = (partial: Record<string, unknown>) => {
	const current = reduxPersistor.getData('app');
	reduxPersistor.update('app', { ...current, ...partial });
};

export const handleBucketSignedUpload = async ({
	url,
	file,
	contentType,
}: {
	url: string;
	file: File | Blob;
	contentType: string;
}): Promise<UploadQueryReturnValue> => {
	try {
		const res = await fetch(url, {
			method: 'PUT',
			headers: {
				'x-amz-acl': 'public-read',
				'Content-Type': contentType,
			},
			body: file,
		});

		if (!res.ok) {
			return {
				error: {
					status: res.status,
					data: await res.text(),
				} as UploadErrorPayload,
				meta: undefined,
			};
		}

		return { data: undefined, meta: undefined };
	} catch (err) {
		return {
			error: {
				status: 'FETCH_ERROR',
				error: (err as Error).message,
				data: undefined,
			} as UploadErrorPayload,
			meta: undefined,
		};
	}
};

export const formatDisplayString = (value?: string) => (!value || value.toLowerCase().includes('undefined') ? '...' : value);

export const viewHelpArticle = (articleId: string) => () => {
	window.open(`https://help.breet.io/en/articles/${articleId}`, '_blank');
};

export const showNativeNotification = async (title: string, body: string) => {
	const options = {
		body: body,
		icon: '/btLogo.png',
		tag: Date.now().toString(),
		badge: '/btLogo.png',
	};

	if (!('Notification' in window)) {
		console.warn('This browser does not support desktop notifications');
		return;
	}

	let permission = window.Notification.permission;
	if (permission === 'default') {
		permission = await window.Notification.requestPermission();
	}

	if (permission === 'granted' && title) {
		try {
			const registration = await navigator.serviceWorker.ready;

			if ('showNotification' in registration && typeof registration.showNotification === 'function') {
				await registration.showNotification(title, options);
			} else {
				new Notification(title, options);
			}
		} catch (error) {
			console.error('Notification display failed:', error);
		}
	}
};

export const preventFormDefault = (e: FormEvent<HTMLFormElement>) => {
	e.preventDefault();
};

export const scrollToElement = (elementId: string) => {
	const element = document.getElementById(elementId);
	if (element) {
		const isHidden = element.offsetWidth === 0 && element.offsetHeight === 0;
		if (isHidden) {
			const label = document.querySelector(`label[for="${elementId}"]`) as unknown as HTMLElement | undefined;
			if (label) {
				label.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
				return;
			}
		}
		element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
	}
};

type Primitive = string | number | boolean | null | undefined;
type FormValue = Primitive | File | FormValue[] | { [key: string]: FormValue };

export const cleanFormValues = (obj: FormValue): FormValue => {
	if (obj instanceof File) return '';

	if (Array.isArray(obj)) {
		return obj.map(cleanFormValues);
	}

	if (obj && typeof obj === 'object') {
		const keys = Object.keys(obj);
		const isFileObject =
			keys.every((k) => /^\d+$/.test(k)) &&
			Object.values(obj).every(
				(v) => v == null || v instanceof File || (typeof v === 'object' && Object.keys(v as object).length === 0)
			);

		if (isFileObject) return '';

		const result: Record<string, FormValue> = {};
		for (const [key, value] of Object.entries(obj)) {
			result[key] = cleanFormValues(value);
		}
		return result;
	}

	return obj;
};

export const validateWalletAddress = (address: string, network: string): boolean => {
	if (!address) return false;

	const lowerNetwork = network.toLowerCase();

	const evmNetworks = ['ethereum', 'erc20', 'bsc', 'binance', 'polygon', 'matic'];
	if (evmNetworks.includes(lowerNetwork)) {
		return /^0x[a-fA-F0-9]{40}$/.test(address);
	}

	if (['tron', 'trc20'].includes(lowerNetwork)) {
		return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(address);
	}

	if (['solana', 'sol'].includes(lowerNetwork)) {
		return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);
	}

	if (['bitcoin', 'btc'].includes(lowerNetwork)) {
		return (
			/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address) ||
			/^bc1[ac-hj-np-zAC-HJ-NP-Z02-9]{11,71}$/.test(address) ||
			/^2[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address)
		);
	}

	return true;
};

export const getNetworkFee = (
	selectedToken: TokenType,
	network: NetworkType,
	usdWithdrawalMeta?: IAuthSettingsUSDWithdrawalMeta
) => {
	try {
		if (!usdWithdrawalMeta) return 0;

		const networkFees = usdWithdrawalMeta.vip.fees[selectedToken];

		return networkFees[network];
	} catch {
		console.error('Network fee not found');
		return 0;
	}
};

export const filterDisabledNetwork =
	(usdWithdrawalMeta: IAuthSettingsUSDWithdrawalMeta, selectedToken: TokenType) =>
	(networkItem: { network: NetworkType; avatar: string }) => {
		try {
			const network = networkItem.network;
			const isNetworkEnabled = usdWithdrawalMeta.vip.networks[network].enabled;
			const tokenNetworks = usdWithdrawalMeta.vip.tokens[selectedToken].networks;
			const isEnabledForToken = network in tokenNetworks ? tokenNetworks[network].enabled : true;
			return isNetworkEnabled && isEnabledForToken;
		} catch (err) {
			console.error(err);
			return false;
		}
	};

export const filterDisabledToken =
	(usdWithdrawalMeta?: IAuthSettingsUSDWithdrawalMeta) => (tokenItem: IWalletInfoOptionProps) => {
		try {
			const token = usdWithdrawalMeta?.vip.tokens[tokenItem.token];
			return token?.enabled;
		} catch (err) {
			console.error(err);
			return false;
		}
	};

export const truncateWithEllipsis = (str: string, start = 9, end = 8) => {
	if (!str) return '';
	if (str.length <= start + end) return str;
	return `${str.substring(0, start)}.....${str.substring(str.length - end)}`;
};

export const convertFxRate = (baseRate: number, valueArg: number, source: 'from' | 'to') => {
	const value = valueArg || 0;
	if (source === 'from') return baseRate * value;

	return value / baseRate;
};

export const cleanRate = (rateString: string): number => {
	if (!rateString) return NaN;

	const cleaned = rateString
		.trim()
		.replace(/[$€£¥₦,\s]/g, '')
		.replace(/,/g, '.');

	return Number(cleaned);
};

export const PAGINATION_DOTS = 5.983548;
export const paginationRangeHandler = (start: number, end: number) => {
	const length = end - start + 1;
	/*
		Create an array of certain length and set the elements within it from
		start value to end value.
	*/
	return Array.from({ length }, (_, idx) => idx + start);
};

export const createFormStageContext = <T extends string>() => {
	const ContextComponent = createContext<IUseFormStage<T> | null>(null);

	const useFormStageContext = (): IUseFormStage<T> => {
		const formStageContext = useContext(ContextComponent);

		if (!formStageContext) {
			throw new Error('useFormStageContext must be used within a FormStageContext.Provider');
		}

		return formStageContext;
	};

	return {
		ContextComponent,
		useFormStageContext,
	};
};

export const prohibitedCryptoKeywords = [
	'bitcoin',
	'btc',
	'ethereum',
	'eth',
	'crypto',
	'cryptocurrency',
	'blockchain',
	'defi',
	'nft',
	'altcoin',
	'token',
	'coin',
	'wallet',
	'exchange',
	'trading',
	'mining',
	'hodl',
	'satoshi',
	'litecoin',
	'ltc',
	'ripple',
	'xrp',
	'dogecoin',
	'doge',
	'binance',
	'coinbase',
	'p2p',
	'peer-to-peer',
	'arbitrage',
	'solana',
	'sol',
	'cardano',
	'ada',
	'polkadot',
	'dot',
	'polygon',
	'matic',
	'avalanche',
	'avax',
	'chainlink',
	'link',
	'stellar',
	'xlm',
	'near',
	'optimism',
	'op',
	'arbitrum',
	'arb',
	'tron',
	'trx',
	'toncoin',
	'ton',
	'kaspa',
	'kas',
	'bitcoin cash',
	'bch',
	'shiba inu',
	'shib',
	'uniswap',
	'uni',
	'monero',
	'xmr',
	'cosmos',
	'atom',
	'aptos',
	'apt',
	'sui',
	'pepe',
	'bonk',
	'floki',
	'wif',
	'tether',
	'usdt',
	'usdc',
	'dai',
	'fdusd',
	'tusd',
	'pyusd',
	'usde',
	'cngn',
	'busd',
	'bybit',
	'kucoin',
	'okx',
	'luno',
	'quidax',
	'paxful',
	'remitano',
	'yellowcard',
	'bundle',
	'busha',
	'noones',
	'kraken',
	'gateio',
	'mexc',
	'bitget',
	'exchanger',
	'swap',
	'staking',
	'yield',
	'dex',
	'cex',
	'otc',
	'liquidity',
	'rate',
	'gas fee',
	'airdrop',
	'seed phrase',
	'private key',
	'smart contract',
];

export const narrationValidation = yup
	.string()
	.required('Narration is required')
	.test('no-prohibited-keywords', 'Narration should not include crypto related words', (value) => {
		if (!value) return true;

		const normalizedValue = value.toLowerCase().replace(/[^a-z0-9]/g, '');

		const isProhibited = prohibitedCryptoKeywords.some((keyword) => {
			const cleanKeyword = keyword.toLowerCase().replace(/[^a-z0-9]/g, '');
			return normalizedValue.includes(cleanKeyword);
		});

		return !isProhibited;
	});

export const PLATFORMFEEPERCENTLABEL = {
	LESS_THAN_1000: '0 - $999',
	BETWEEN_1000_AND_4999: '$1000 - $4999',
	ABOVE_5000: 'Above $5000',
};

export const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError =>
	typeof error === 'object' && error != null && 'status' in error;

export const webhookLogEventTypeObj: Record<IWebhookLogsEventType, string> = {
	'trade.pending': 'Pending Trade',
	'trade.flagged': 'Flagged Trade',
	'trade.completed': 'Completed Trade',
	'withdrawal.pending': 'Pending Withdrawal',
	'withdrawal.processing': 'Processing Withdrawal',
	'withdrawal.completed': 'Completed Withdrawal',
};

export const getWebhooklogEventType = (type: IWebhookLogsEventType) => webhookLogEventTypeObj[type];

export const webhookLogStatusObj: Record<IWebhookLogsStatusType, IAppStatus> = {
	delivered: 'successful',
	failed: 'unsuccessful',
};

export const getWebhooklogStatus = (status: IWebhookLogsStatusType) => webhookLogStatusObj[status];

export const cryptoSettingsOptions = [
	{
		currency: 'ngn',
		description: 'Your crypto deposits will be automatically converted to Naira and deposited to your Naira wallet.',
	},
	{
		currency: 'ghs',
		description: 'Your crypto deposits will be automatically converted to Cedis and deposited to your Cedis wallet.',
	},
	{
		currency: 'usd',
		description: 'The USD equivalent of your crypto deposits will be deposited to your USD wallet.',
	},
] as const;

export const getFxConversionType = (fromCurrencyCode: CURRENCYCODETYPE, toCurrencyCode: CURRENCYCODETYPE): FXCONVERSION_TYPE => {
	switch (fromCurrencyCode) {
		case 'USD':
			return FXCONVERSION.SELLUSD;
		case 'NGN':
			if (toCurrencyCode === 'GHS') {
				return FXCONVERSION.NGNToGHS;
			}
			return FXCONVERSION.FIATToUSD;
		case 'GHS':
			if (toCurrencyCode === 'NGN') {
				return FXCONVERSION.GHSToNGN;
			}
			return FXCONVERSION.FIATToUSD;
		default:
			return FXCONVERSION.FIATToUSD;
	}
};

export const getSafeObjectField = <T extends object, K extends keyof T, D>(
	obj: T | undefined | null,
	path: K,
	defaultValue?: D
): T[K] | D => {
	const value = obj && path in obj ? obj[path] : undefined;
	return (value ?? defaultValue) as T[K] | D;
};

export const convertCryptoAmount = (value: string | number | null | undefined) => {
	if (value === null || value === undefined || value === '') return '—';
	const num = Number(value);
	if (Number.isNaN(num)) return '—';
	return num.toLocaleString('en-US', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 20,
	});
};

export const convertCryptoInputAmount = (value: string | number | null | undefined) => {
	if (value === null || value === undefined || value === '') return '—';
	const num = Number(value);
	if (Number.isNaN(num)) return '0';
	return num.toLocaleString('en-US', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 7,
	});
};

export const NewDeviceLoginSchema = yup.object({
	otp: otpValidation,
	recoveryCode: yup
		.string()
		.notRequired()
		.test('recovery-code-digits', 'Enter a 10-character alphanumeric recovery code', (value) => {
			if (value == null || value === '') return true;
			return /^[A-Za-z0-9]{10}$/.test(value.trim());
		}),
});
